import { orderAllocationDelivery } from 'api/mdl21'
import { useState } from 'react'
import { sourceMap } from 'Utils/constants'

/**
 * Hook personalizado para manejar la integración con Order Allocation (OA)
 * @param {Object} options - Opciones de configuración
 * @param {string} options.documentId - ID del documento (QuoteHeaderId, etc.)
 * @param {string} options.companyCode - Código de la compañía
 * @param {Array} options.products - Array de productos
 * @returns {Object} Estados y funciones del hook
 */
export const useOrderAllocation = ({ documentId, companyCode, products }) => {
  const [oaLoading, setOaLoading] = useState(false)
  const [oaError, setOaError] = useState(null)
  const [oaResponse, setOaResponse] = useState(null)

  /**
   * Construye el payload para OA basado en la dirección seleccionada
   * @param {Object} address - Dirección seleccionada
   * @returns {Object} Payload para OA
   */
  const buildOAPayload = address => {
    // Validar y obtener campos address OA con fallbacks
    const oaAddress = {
      province:
        address.AddressInfo?.ProvinceName ||
        (typeof address.AddressInfo?.Province === 'object'
          ? address.AddressInfo?.Province?.Name
          : address.AddressInfo?.Province) ||
        address.AddressInfo?.Address_ProvinceName,
      district:
        address.AddressInfo?.DistrictName ||
        (typeof address.AddressInfo?.District === 'object'
          ? address.AddressInfo?.District?.Name
          : address.AddressInfo?.District) ||
        address.AddressInfo?.Address_DistrictName,
      township:
        address.AddressInfo?.TownshipName ||
        (typeof address.AddressInfo?.Township === 'object'
          ? address.AddressInfo?.Township?.Name
          : address.AddressInfo?.Township) ||
        address.AddressInfo?.Address_TownshipName,
      street:
        address.AddressInfo?.Street ||
        address.AddressInfo?.Address_Street ||
        address.AddressInfo?.AddressDetail ||
        address.AddressInfo?.Address_AddressDetail ||
        address.AddressInfo?.QuoteAddressDetail,
    }

    return {
      documentId: documentId || '00000000-0000-0000-0000-000000000000',
      companyCode,
      products: products.map(p => ({ sku: p.Sku, qty: p.Quantity })),
      latitude:
        address.AddressInfo?.Latitude || address.AddressInfo?.Address_Latitude,
      longitude:
        address.AddressInfo?.Longitude ||
        address.AddressInfo?.Address_Longitude,
      address: oaAddress,
      source: sourceMap[companyCode] || '',
      storeCode: '',
      type: 'b2c',
    }
  }

  /**
   * Valida que la dirección tenga todos los campos requeridos para OA
   * @param {Object} address - Dirección a validar
   * @returns {Object} Resultado de la validación
   */
  const validateAddressForOA = address => {
    const oaAddress = buildOAPayload(address).address

    const missingFields = []
    if (!oaAddress.province) missingFields.push('provincia')
    if (!oaAddress.district) missingFields.push('distrito')
    if (!oaAddress.township) missingFields.push('corregimiento')
    if (!oaAddress.street) missingFields.push('calle')

    if (missingFields.length > 0) {
      return {
        isValid: false,
        error: `La dirección seleccionada está incompleta. Faltan: ${missingFields.join(
          ', '
        )}. Por favor, edítala y asegúrate de que tiene todos los campos requeridos.`,
      }
    }

    return { isValid: true }
  }

  /**
   * Ejecuta la consulta a OA para entrega a domicilio
   * @param {Object} address - Dirección seleccionada
   * @returns {Promise<Object>} Respuesta de OA
   */
  const executeOADelivery = async address => {
    setOaLoading(true)
    setOaError(null)
    setOaResponse(null)

    try {
      // Validar dirección
      const validation = validateAddressForOA(address)
      if (!validation.isValid) {
        setOaError(validation.error)
        return null
      }

      // Construir payload
      const payload = buildOAPayload(address)

      // Llamar servicio OA
      const response = await orderAllocationDelivery(payload)
      setOaResponse(response)

      return response
    } catch (err) {
      const errorMessage =
        'Error al consultar la logística óptima. Intente nuevamente.'
      setOaError(errorMessage)
      return null
    } finally {
      setOaLoading(false)
    }
  }

  /**
   * Extrae la fecha mínima sugerida desde la respuesta de OA
   * @param {Object} oaResponse - Respuesta de OA
   * @returns {string|null} Fecha mínima en formato ISO string
   */
  const getMinDateFromOA = oaResponse => {
    return oaResponse?.data?.fromAddress?.estimateDate || null
  }

  /**
   * Extrae la sucursal sugerida desde la respuesta de OA
   * @param {Object} oaResponse - Respuesta de OA
   * @returns {Object|null} Información de la sucursal sugerida
   */
  const getSuggestedBranchFromOA = oaResponse => {
    const fromAddress = oaResponse?.data?.fromAddress
    if (!fromAddress) return null

    return {
      branchId: fromAddress.Code,
      branchName: fromAddress.name,
      addressId: fromAddress.addressFiscalCode,
      Description: fromAddress.name,
      WarehouseId: fromAddress.Code,
      AddressId: fromAddress.addressFiscalCode,
    }
  }

  /**
   * Limpia los estados del hook
   */
  const clearOAStates = () => {
    setOaLoading(false)
    setOaError(null)
    setOaResponse(null)
  }

  return {
    // Estados
    oaLoading,
    oaError,
    oaResponse,
    setOaError,

    // Funciones
    executeOADelivery,
    validateAddressForOA,
    buildOAPayload,
    getMinDateFromOA,
    getSuggestedBranchFromOA,
    clearOAStates,
  }
}
